import React, { useState, useEffect, useRef } from 'react';
import './Gallery.css';
import { UpArrow, DownArrow } from './ScrollArrows';
import { applyPolishTypography, parseSpecialMarkup } from '../utils/typography';

// Helper to fetch local JSON files
async function fetchCategoryData(filename) {
  const res = await fetch(process.env.PUBLIC_URL + `/descriptions/${filename}`);
  return res.json();
}

export default function Gallery({ language = 'en' }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Listen to hash changes for navigation
  useEffect(() => {
    function handleHashChange() {
      const hash = window.location.hash.replace(/^#/, '');
      if (hash && categories.some(cat => cat.id === hash)) {
        setSelectedCategory(hash);
      } else {
        setSelectedCategory('all');
      }
    }
    window.addEventListener('hashchange', handleHashChange);
    // Initial check
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [categories]);

  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/descriptions/categories-manifest.json')
      .then(res => res.json())
      .then(async cats => {
        setCategories(cats);
        // Load all items from all categories
        const allItems = [];
        for (const cat of cats) {
          const data = await fetchCategoryData(cat.filename);
          allItems.push(...data.map(item => ({ ...item, category: cat, categoryId: cat.id })));
        }
        setItems(allItems);
      });
  }, []);

  const scrollRef = useRef();
  const [showUp, setShowUp] = useState(false);
  const [showDown, setShowDown] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      setShowUp(el.scrollTop > 10);
      setShowDown(el.scrollTop + el.clientHeight < el.scrollHeight - 10);
    };
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', handleScroll);
    handleScroll(); // initialize
    return () => el && el.removeEventListener('scroll', handleScroll);
  }, [items]);

  const scrollBy = (delta) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ top: delta, behavior: 'smooth' });
    }
  };


  const filteredItems = selectedCategory === 'all' ? items : items.filter(item => item.categoryId === selectedCategory);

  return (
    <>
      <h1 style={{color:'red',textAlign:'center'}}>DEBUG: This is the NEW Gallery.js</h1>
      <section className="gallery-section" id="gallery">
      {/* Category Filter Buttons */}
      <div style={{display: 'flex', gap: '0.7rem', marginBottom: '1.6rem', flexWrap: 'wrap'}}>
        <button
          className={selectedCategory === 'all' ? 'gallery-category-btn active' : 'gallery-category-btn'}
          onClick={() => setSelectedCategory('all')}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            className={selectedCategory === cat.id ? 'gallery-category-btn active' : 'gallery-category-btn'}
            onClick={() => setSelectedCategory(cat.id)}
          >
            {cat.name[language]}
          </button>
        ))}
      </div>
      <div className="gallery-scroll-container" ref={scrollRef}>
        {selectedCategory && selectedCategory !== 'all' && (
          <button
            className="gallery-back-arrow"
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              background: '#0D5EAF',
              border: 'none',
              borderRadius: '24px',
              minWidth: 80,
              height: 48,
              color: '#fff',
              fontSize: 18,
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(13,94,175,0.2)',
              cursor: 'pointer',
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 24px',
              overflow: 'visible',
            }}
            onClick={() => {
              window.location.hash = '';
            }}
            aria-label={language === 'pl' ? 'Powrót do kategorii' : 'Back to categories'}
          >
            {language === 'pl' ? 'Powrót' : 'Back'}
          </button>
        )}
        {selectedCategory === 'all' && (
          <button
            className="gallery-back-arrow"
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              background: '#0D5EAF',
              border: 'none',
              borderRadius: '24px',
              minWidth: 80,
              height: 48,
              color: '#fff',
              fontSize: 18,
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(13,94,175,0.2)',
              cursor: 'pointer',
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 24px',
              overflow: 'visible',
            }}
            onClick={() => {
              window.location.hash = '';
            }}
            aria-label={language === 'pl' ? 'Powrót do kategorii' : 'Back to categories'}
          >
            {language === 'pl' ? 'Powrót' : 'Back'}
          </button>
        )}
        <div className="gallery-grid">
          {filteredItems.map((item, idx) => (
            <div className="gallery-card" key={idx} onClick={() => setSelected(item)}>
              <img src={process.env.PUBLIC_URL + '/' + (item.file || '')} alt={(item.title && item.title[language]) || ''} className="gallery-img" />
              <div className="gallery-card-content">
                <h3>{(item.title && item.title[language]) || ''}</h3>
                {(() => {
                  const desc = (item.description && item.description[language]) || '';
                  const isPolish = language === 'pl';
                  const formatted = isPolish ? applyPolishTypography(desc) : desc;
                  return <p dangerouslySetInnerHTML={{ __html: parseSpecialMarkup(formatted) }} />;
                })()}
                <span className="gallery-category">{(item.category && item.category.name && item.category.name[language]) || ''}</span>
              </div>
            </div>
          ))}
        </div>
        <UpArrow visible={showUp} onClick={() => scrollBy(-window.innerHeight * 0.6)} />
        <DownArrow visible={showDown} onClick={() => scrollBy(window.innerHeight * 0.6)} />
      </div>
      {selected && (
        <div className="gallery-modal" onClick={() => setSelected(null)}>
          <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
            <img src={process.env.PUBLIC_URL + '/' + selected.file} alt={selected.title[language]} className="gallery-modal-img" />
            <div className="gallery-modal-details">
              <h2>{selected.title[language]}</h2>
              {(() => {
                const desc = selected.description[language] || '';
                const isPolish = language === 'pl';
                const formatted = isPolish ? applyPolishTypography(desc) : desc;
                return <p dangerouslySetInnerHTML={{ __html: parseSpecialMarkup(formatted) }} />;
              })()}
              <span className="gallery-category">{selected.category.name[language]}</span>
            </div>
            <button className="gallery-modal-close" onClick={() => setSelected(null)}>&times;</button>
          </div>
        </div>
      )}
    </section>
    </>
  );
}
